/**
 * Parcelas a receber: situação em relação a hoje, confirmação de recebimento e data editável.
 * As parcelas nascem sozinhas do aceite da proposta e da assinatura do contrato.
 */
import { useEffect, useState } from 'react';
import { dateBr, diffDays, type Receivable } from '@dotapps/core';
import { Icon, NumberField, SheetFoot, SheetHead, TextField, useDialog } from '@dotapps/ui';
import { api } from '../../lib/api';
import { useLookups, useToday } from '../../lib/hooks';
import { keys, useAction } from '../../lib/queries';

/** Situação de uma parcela pendente em relação a hoje. */
export function dueStatus(r: Receivable, today: string): { text: string; tone?: 'bad' | 'warn' | 'ok' } {
  if (r.receivedAt) return { text: `Recebido em ${dateBr(r.receivedAt)}`, tone: 'ok' };
  const d = diffDays(today, r.dueDate);
  if (d < 0) return { text: `Atrasado ${-d} ${d === -1 ? 'dia' : 'dias'}`, tone: 'bad' };
  if (d === 0) return { text: 'Vence hoje', tone: 'warn' };
  if (d === 1) return { text: 'Vence amanhã', tone: 'warn' };
  return { text: `em ${d} dias` };
}

/** Alerta da plataforma: pendente que vence até amanhã (ou já venceu). */
export const needsAttention = (r: Receivable, today: string) => !r.receivedAt && diffDays(today, r.dueDate) <= 1;

export function ReceivedDialog({ r }: { r: Receivable }) {
  const { close } = useDialog();
  const today = useToday();
  const look = useLookups();
  const [date, setDate] = useState(r.dueDate <= today ? r.dueDate : today);
  const [valor, setValor] = useState(r.valor);
  const save = useAction(
    () => api.post<Receivable>(`/receivables/${r.id}/received`, { date, valor: Math.max(0, valor || 0) }),
    { invalidate: [keys.receivables, keys.deals], success: 'Recebimento confirmado' },
  );
  return (
    <>
      <SheetHead title="Confirmar recebimento" sub={`${look.clientName(r.clientId)} · ${r.label}`} />
      <div className="sheet-body form">
        <div className="form-grid">
          <TextField label="Data em que caiu na conta" type="date" value={date} onValue={setDate} />
          <NumberField label="Valor recebido (R$)" min={0} step={10} value={valor} onValue={setValor} />
        </div>
        <small className="muted">O evento sai da Google Agenda dos administradores.</small>
        <SheetFoot>
          <button className="btn ghost" onClick={close}>
            Cancelar
          </button>
          <button
            className="btn primary"
            disabled={save.pending || !date}
            onClick={async () => {
              await save(undefined);
              close();
            }}
          >
            <Icon name="check" />
            Recebido
          </button>
        </SheetFoot>
      </div>
    </>
  );
}

/** Data da parcela editável: grava ao escolher (e move o evento na Google Agenda). */
export function DueInput({ r }: { r: Receivable }) {
  const [v, setV] = useState(r.dueDate);
  useEffect(() => {
    setV(r.dueDate);
  }, [r.dueDate]);
  const save = useAction((dueDate: string) => api.patch<Receivable>(`/receivables/${r.id}`, { dueDate }), {
    invalidate: [keys.receivables],
    success: 'Data de pagamento atualizada',
  });
  return (
    <input
      type="date"
      className="rc-date"
      value={v}
      aria-label={`Data de pagamento de ${r.label}`}
      onChange={e => setV(e.target.value)}
      onBlur={() => v && v !== r.dueDate && save(v).catch(() => setV(r.dueDate))}
    />
  );
}
